import type {
  PlatformFilterDto,
  PlatformFilterDtoStatesItem,
  PredicateDto,
} from '../generated/social-media/metapicSocialMediaAPIDocumentation.schemas'

import { type CriteriaChild, isPlatformFilter } from './segment-criteria-nodes'

export enum CriteriaConflictEnum {
  EmptyFollowerRange = 'emptyFollowerRange',
  NoCommonState = 'noCommonState',
}

/** Nodes for one platform whose conditions no single account can meet together. */
export type CriteriaConflict = {
  reason: CriteriaConflictEnum
  /** Only the nodes taking part in the contradiction, not every node for the platform. */
  criterionIds: string[]
}

export type MergedChildren = { children: CriteriaChild[]; conflicts: CriteriaConflict[] }

type PlatformGroup = [PlatformFilterDto, ...PlatformFilterDto[]]
type PlatformMerge = { node: PlatformFilterDto } | { conflict: CriteriaConflict }
type FollowerBounds = { min?: number; max?: number }

const conflictOf = (reason: CriteriaConflictEnum, filters: PlatformFilterDto[]): PlatformMerge => ({
  conflict: { reason, criterionIds: filters.map(({ id }) => id) },
})

/** Only the states every node allows. Undefined when no node restricts them. */
const intersectStates = (
  filters: PlatformFilterDto[],
): PlatformFilterDtoStatesItem[] | undefined => {
  const [first, ...rest] = filters.flatMap(({ states }) => (states ? [states] : []))

  return (
    first &&
    rest.reduce((common, states) => common.filter((state) => states.includes(state)), first)
  )
}

/** The floor or ceiling one row's predicate sets. The form only builds single bounds. */
const boundsOf = (predicate?: PredicateDto): FollowerBounds => {
  switch (predicate?.op) {
    case 'gte':
      return { min: predicate.value }
    case 'lte':
      return { max: predicate.value }
    default:
      return {}
  }
}

const definedValues = (values: (number | undefined)[]): number[] =>
  values.filter((value) => value !== undefined)

/** The highest floor and the lowest ceiling across the nodes. */
const tightestBounds = (filters: PlatformFilterDto[]): FollowerBounds => {
  const bounds = filters.map(({ predicate }) => boundsOf(predicate))
  const floors = definedValues(bounds.map(({ min }) => min))
  const ceilings = definedValues(bounds.map(({ max }) => max))

  return {
    min: floors.length > 0 ? Math.max(...floors) : undefined,
    max: ceilings.length > 0 ? Math.min(...ceilings) : undefined,
  }
}

const toPredicate = ({ min, max }: FollowerBounds): PredicateDto | undefined => {
  if (min !== undefined && max !== undefined) {
    return { op: 'between', min, max }
  }

  if (min !== undefined) {
    return { op: 'gte', value: min }
  }

  return max !== undefined ? { op: 'lte', value: max } : undefined
}

const mergePlatform = (group: PlatformGroup): PlatformMerge => {
  const [first] = group

  if (group.length === 1) {
    return { node: first }
  }

  const states = intersectStates(group)
  // Undefined means no node asked about status. Only nodes that each did, with no status in common, clash.
  const hasNoCommonState = states === undefined ? false : states.length === 0

  if (hasNoCommonState) {
    return conflictOf(
      CriteriaConflictEnum.NoCommonState,
      group.filter(({ states: nodeStates }) => nodeStates),
    )
  }

  const bounds = tightestBounds(group)

  // The API needs a range as min < max, so a single-value range is empty too.
  if (bounds.min !== undefined && bounds.max !== undefined && bounds.min >= bounds.max) {
    return conflictOf(
      CriteriaConflictEnum.EmptyFollowerRange,
      group.filter(({ predicate }) => predicate),
    )
  }

  const predicate = toPredicate(bounds)

  return {
    node: {
      kind: 'filter.platform',
      // The preview keys its matched values by node id, so the node keeps a row's id to map back to.
      id: first.id,
      platform: first.platform,
      ...(states && { states }),
      ...(predicate && { predicate }),
    },
  }
}

/** Each platform's nodes, in the order its first node appears. */
const groupByPlatform = (filters: PlatformFilterDto[]): PlatformGroup[] =>
  filters
    .filter(
      (filter, index) =>
        filters.findIndex(({ platform }) => platform === filter.platform) === index,
    )
    .map((first) => [
      first,
      ...filters.filter((filter) => filter !== first && filter.platform === first.platform),
    ])

/**
 * The API evaluates every node on its own, and any of the creator's accounts may satisfy it. Two
 * nodes for one platform would let a 5k account meet "≤ 20k" and a 50k account meet "≥ 10k".
 * Merged into one node, a single account has to meet every condition. This only holds under AND —
 * under OR, "either account" is what separate nodes mean — so callers pass an AND group's children.
 *
 * The merged node takes the place of the platform's first node. A platform whose nodes contradict
 * each other is left out and reported instead.
 */
export const mergeSamePlatformNodes = (children: CriteriaChild[]): MergedChildren => {
  const merges = groupByPlatform(children.filter(isPlatformFilter)).map((group) => ({
    first: group[0],
    result: mergePlatform(group),
  }))

  return {
    children: children.flatMap<CriteriaChild>((child) => {
      if (!isPlatformFilter(child)) {
        return [child]
      }

      const merge = merges.find(({ first }) => first === child)

      return merge && 'node' in merge.result ? [merge.result.node] : []
    }),
    conflicts: merges.flatMap(({ result }) => ('conflict' in result ? [result.conflict] : [])),
  }
}
